import React from "react";
import styled, { css } from "styled-components/macro";
import {
  DPIconLeftArrow,
  DPIconRightArrow,
  DPIconLoadingSpinner,
} from "../assets/icons";
import { Link, useHistory } from "react-router-dom";
import Theme from "../constants/Theme";
import Typography from "./typography";

const { Text } = Typography;

type IconTypes = {
  left?: boolean;
  right?: boolean;
  icon?: React.ReactElement;
};

const showArrows = ({ left, right, icon }: IconTypes) => {
  if (left) return <DPIconLeftArrow />;
  if (right) return <DPIconRightArrow />;
  if (icon) return icon;
  return "";
};

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  bordered?: boolean;
  noShadow?: boolean;
  text?: string | React.ReactElement;
  primary?: boolean;
  secondary?: boolean;
  warning?: boolean;
  success?: boolean;
  fullWidth?: boolean;
}

type ButtonNavigationComponent = React.FC<{
  left?: boolean;
  right?: boolean;
  icon?: React.ReactElement;
  backgroundColor?: string;
  color?: string;
  className?: string;
  onClick?: any;
  id?: any;
}>;
type DashboardBackButtonComponent = React.FC<{
  text: string;
  to?: string;
  back?: boolean;
}>;

type OnlyOneTypeAndButtonProps = RequireOnlyOne<
  ButtonProps,
  "success" | "secondary" | "warning" | "primary"
>;

type ButtonComponent = React.FC<OnlyOneTypeAndButtonProps> & {
  Navigation: ButtonNavigationComponent;
  DashboardBack: DashboardBackButtonComponent;
};

type RequireOnlyOne<T, Keys extends keyof T = keyof T> = Pick<
  T,
  Exclude<keyof T, Keys>
> &
  {
    [K in Keys]-?: Required<Pick<T, K>> &
      Partial<Record<Exclude<Keys, K>, undefined>>;
  }[Keys];

const Button: ButtonComponent = (props) => {
  const { loading, children, ...rest } = props;
  return (
    <ButtonStyle {...rest}>
      {loading ? <DPIconLoadingSpinner /> : props.text || children}
    </ButtonStyle>
  );
};

const ButtonNavigation: ButtonNavigationComponent = (props) => {
  const { children, ...rest } = props;
  return (
    <NavButtonWrapper {...rest}>
      <NavButtonStyle>{showArrows(props)}</NavButtonStyle>
      <span className="nav__text">{children}</span>
    </NavButtonWrapper>
  );
};

Button.Navigation = ButtonNavigation;

const DashboardPrevButton: DashboardBackButtonComponent = ({
  text,
  to,
  back,
}) => {
  const { goBack } = useHistory();
  return (
    <>
      {back ? (
        <span
          role="presentation"
          className="dashboard--go-back"
          onClick={goBack}
        >
          <DPIconLeftArrow />
          <Text>{text}</Text>
        </span>
      ) : (
        <Link className="dashboard--go-back" to={to || "/"}>
          <DPIconLeftArrow />
          <Text>{text}</Text>
        </Link>
      )}
    </>
  );
};

Button.DashboardBack = DashboardPrevButton;

const NavButtonWrapper = styled.div`
  width: 45px;
  display: flex;
  flex-direction: column;
  align-items: center;

  .nav {
    &__text {
      font-size: ${Theme.fontSizes.small};
    }
  }
`;

const ButtonStyle = styled.button<any>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  > svg,
  img {
    display: flex;
  }
  border: none;
  /* height: 50px; */
  border-radius: 4px;
  padding: 15px 25px;
  color: ${Theme.colorPalette.white};
  opacity: ${({ disabled }) => (disabled ? "0.4" : "1")};
  pointer-events: ${({ disabled }) => (disabled ? "none" : "unset")};
  font-size: ${Theme.fontSizes.regular};
  font-weight: ${Theme.fontWeights.semiBold};
  font-family: ${Theme.typography.secondary};
  box-shadow: ${({ noShadow }) =>
    noShadow ? "none" : Theme.colorPalette.buttonShadow};
  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}
  ${({ primary, secondary, warning, success, bordered }) => {
    if (bordered || secondary) {
      return css`
        border: 1px solid ${Theme.colorPalette.darkBlue};
        color: ${Theme.colorPalette.darkBlue};
        background: transparent;
      `;
    }
    if (primary) {
      return css`
        background-color: ${Theme.colorPalette.darkBlue};
      `;
    }
    if (warning) {
      return css`
        background-color: ${Theme.colorPalette.warning};
      `;
    }
    if (success) {
      return css`
        background-color: ${Theme.colorPalette.success};
      `;
    }
    return css`
      background-color: brown;
    `;
  }}

  transition: transform 0.5s;

  &:hover {
    transform: scale(1.05);
  }
  &:active {
    transform: scale(1);
    transition: transform 0.2s;
  }
`;

const NavButtonStyle = styled.button`
  width: 45px;
  height: 45px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  outline: none;
  border-radius: 10px;
  color: ${({ color }) => color || undefined};
  background: none;
  margin-bottom: 5px;
  /* border: 1px solid red; */

  svg {
    width: 22px;
    height: 16px;
  }
`;

export default Button;
